import {
  DEFAULT_LADDERS_STYLE,
  LADDERS_PICTURE_STYLES,
  LADDERS_STYLES,
  type LaddersStyle,
} from "@bing/engine-ladders";
import { CLASSIC_BOARD_TONES, LADDERS_BOARD_TONES, type BoardTones } from "./looks";
import { sceneSources, type ScenePictureSources } from "./prefetch";

/* ------------------------------------------------------------------ *
 * مار پله — the LOOKS, data-driven (card-86 round 2, batch W1; design
 * §9.3.13). One entry per look the engine names (`LADDERS_STYLES`):
 *
 *   · a PICTURE look (`jungle`, `bg2`) = its picture + its measurements
 *     JSON (`tools/card-art/ladders-scene.cjs`: `master`, the measured
 *     `board`, the focal `band`, the nullable `temple` (the landmark's
 *     foot), `bg.variants`, `fill`, `edges`) — found by `import.meta.glob`,
 *     hashed Vite asset imports, as in round 1;
 *   · «اورجینال» (`classic`) = no picture: a plain backdrop from theme
 *     tokens and classic alternating cells.
 *
 * ⚠️ ABSENCE-TOLERANT. A picture look is drawable only when BOTH its
 * measurements and its JPEG variants are in the tree (round 3: the
 * resolution ladder `scene-<stem>-w<px>.jpg` + its fill, found by
 * `prefetch.ts`'s `sceneSources` — the ONE discovery the preload shares);
 * a WebP is an optional twin per variant. So a new picture slots in as its
 * files + `tools/card-art/ladders-scene-<id>-measurements.json` with NO code
 * change. A style this build cannot draw — an unknown id, or a look whose
 * files are not here — draws jungle. Jungle keeps its round-1 stem
 * (`scene-bg*`, `ladders-scene-measurements.json`), mapped explicitly below.
 * ------------------------------------------------------------------ */

/** A picture's measurements, in its MASTER's px (`ladders-scene.cjs`; `calm` is retired). */
export interface SceneMeasurements {
  readonly master: { readonly w: number; readonly h: number };
  /** The board's box in the picture: its top-left anchor and its natural width (a square). */
  readonly board: { readonly x: number; readonly y: number; readonly w: number };
  /** The focal band over the board: the spire's tip and x (over the 100 cell), the temple's right. */
  readonly band: {
    readonly top: number;
    readonly spireTip: number;
    readonly spireX: number;
    readonly templeRight: number;
  };
  /**
   * The landmark's foot box (jungle's plinth, measured by hand): its lowest row is where the board
   * may start, so the whole temple stays in view — null: the foot is `board.y` (bg2's railing).
   */
  readonly temple: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  } | null;
  /** The build-time-blurred fill: its size and the px of edge extension on each side. */
  readonly fill: { readonly w: number; readonly h: number; readonly margin: number };
  /** The picture's own edge colours: the placeholder under it until it has loaded. */
  readonly edges: {
    readonly top: string;
    readonly bottom: string;
    readonly left: string;
    readonly right: string;
    readonly topStops: readonly string[];
  };
  /** OPTIONAL (round 2): this picture's own cell tones — absent, the jungle's are drawn. */
  readonly cells?: BoardTones;
}

export interface PictureLook {
  readonly kind: "picture";
  readonly id: LaddersStyle;
  readonly scene: SceneMeasurements;
  /** Its resolution variants (narrowest first, each with its JPEG) and its fill. */
  readonly bg: ScenePictureSources;
  readonly tones: BoardTones;
  /** The CSS background under the picture: its edge colours (the pre-load placeholder). */
  readonly placeholder: string;
}

export interface PlainLook {
  readonly kind: "plain";
  readonly id: LaddersStyle;
  readonly tones: BoardTones;
}

export type LaddersLook = PictureLook | PlainLook;

/** The last path segment, e.g. `scene-bg-w688.jpg`. */
const baseName = (p: string): string => p.slice(p.lastIndexOf("/") + 1);

function byName<T>(found: Record<string, T>): Readonly<Record<string, T>> {
  const out: Record<string, T> = {};
  for (const [p, v] of Object.entries(found)) out[baseName(p)] = v;
  return out;
}

/** Every picture file in the looks' folder, by name — for the stray-file test only. */
const ART = byName(
  import.meta.glob<string>("../../assets/ladders/*.{jpg,jpeg,png,webp}", {
    eager: true,
    query: "?url",
    import: "default",
  }),
);
/** Every picture look's measurements. */
const MEASURED = byName(
  import.meta.glob<SceneMeasurements>("../../../tools/card-art/ladders-scene*-measurements.json", {
    eager: true,
    import: "default",
  }),
);

/** Where a picture look's files are: jungle keeps its round-1 stem, any other look is `<id>`. */
export function pictureFilesOf(id: string): { readonly bg: string; readonly measurements: string } {
  return id === "jungle"
    ? { bg: "scene-bg", measurements: "ladders-scene-measurements.json" }
    : { bg: `scene-${id}`, measurements: `ladders-scene-${id}-measurements.json` };
}

/**
 * A top stop LIGHTER than this (relative luminance) is never drawn by the placeholder: before the
 * picture arrives it would read as a pale strip at the top of the screen — the operator's «white
 * strip». (The jungle's sunlit sky between its temple and its leaves measures #dfe4d9, ≈ 0.76;
 * every other top stop of the two pictures is under 0.2.)
 */
export const PLACEHOLDER_PALE_LUMA = 0.45;

/** The WCAG relative luminance of a `#rrggbb` colour. */
export function lumaOf(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

/** The channel-wise mean of two `#rrggbb` colours. */
const mixHex = (a: string, b: string): string =>
  `#${[1, 3, 5]
    .map((i) =>
      Math.round((parseInt(a.slice(i, i + 2), 16) + parseInt(b.slice(i, i + 2), 16)) / 2)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

/**
 * The placeholder's top stops: the picture's own, but every PALE one drawn as the picture's darker
 * tones beside it — the mean of the nearest darker stop on each side (the jungle's sky becomes its
 * temple's and its leaves' tone), or the left edge's colour if the picture has no darker top stop.
 * A picture with no pale top stop (bg2) keeps its stops exactly as measured.
 */
export function placeholderTopStops(e: SceneMeasurements["edges"]): string[] {
  const pale = (c: string): boolean => lumaOf(c) > PLACEHOLDER_PALE_LUMA;
  return e.topStops.map((c, i) => {
    if (!pale(c)) return c;
    const before = e.topStops
      .slice(0, i)
      .reverse()
      .find((x) => !pale(x));
    const after = e.topStops.slice(i + 1).find((x) => !pale(x));
    return before && after ? mixHex(before, after) : (before ?? after ?? e.left);
  });
}

/** Round 1's base: the picture's own edge colours, under the parts it does not cover yet — never a
 *  pale top ({@link placeholderTopStops}). */
export function placeholderOf(e: SceneMeasurements["edges"]): string {
  return `linear-gradient(to bottom, rgba(0,0,0,0) 0%, ${e.left} 28%, ${e.bottom} 100%), linear-gradient(to right, ${placeholderTopStops(e).join(", ")})`;
}

function pictureLook(id: LaddersStyle): PictureLook | null {
  const scene = MEASURED[pictureFilesOf(id).measurements];
  const bg = sceneSources(id);
  if (!scene || !bg) return null;
  return {
    kind: "picture",
    id,
    scene,
    bg,
    tones: scene.cells ?? LADDERS_BOARD_TONES,
    placeholder: placeholderOf(scene.edges),
  };
}

/** Every look this build can DRAW, by id (a picture look without its files is absent). */
export const LADDERS_LOOKS: Readonly<Partial<Record<LaddersStyle, LaddersLook>>> = (() => {
  const out: Partial<Record<LaddersStyle, LaddersLook>> = {};
  for (const id of LADDERS_STYLES) {
    if (LADDERS_PICTURE_STYLES.includes(id)) {
      const look = pictureLook(id);
      if (look) out[id] = look;
    } else if (id === "classic") {
      out[id] = { kind: "plain", id, tones: CLASSIC_BOARD_TONES };
    }
  }
  return out;
})();

const FALLBACK = LADDERS_LOOKS[DEFAULT_LADDERS_STYLE] ?? LADDERS_LOOKS.jungle;
if (!FALLBACK) throw new Error("مار پله: the jungle look's picture or measurements are missing");
const JUNGLE: LaddersLook = FALLBACK;

/** The look to draw for `style` — an unknown id, or one this build cannot draw, draws jungle. */
export function lookFor(style: unknown): LaddersLook {
  return typeof style === "string" && Object.prototype.hasOwnProperty.call(LADDERS_LOOKS, style)
    ? (LADDERS_LOOKS[style as LaddersStyle] ?? JUNGLE)
    : JUNGLE;
}

/**
 * The picture files in the tree, by name — for the test that keeps a drop-in from half-landing
 * (a measurements file without its picture, a picture no look names).
 */
export const LADDERS_SCENE_FILES: {
  readonly art: readonly string[];
  readonly measurements: readonly string[];
} = { art: Object.keys(ART).sort(), measurements: Object.keys(MEASURED).sort() };
